import React, { useCallback, useEffect } from 'react';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CustomDialog } from 'components/shared/Dialog';
import { Button } from '@mui/material';

import { useCustomerAccount, customersApi } from '../../api';
import { mapFormToCreateInput } from '../../mapper/customerAccount';
import { CustomerAccountFormFields } from './CustomerAccountFormFields';
import { customerFormSchema, CustomerFormValues, customerFormDefaultValues } from './customerFormSchema';

export const CustomerAccountDialog = () => {
  const { isOpen, handleClose } = useModal(ModalsKeys.CustomerAccountDialog);

  const { handleCreateCustomerAccount, isCreating } = useCustomerAccount();

  const { control, handleSubmit, reset } = useForm<CustomerFormValues>({
    resolver: zodResolver(customerFormSchema),
    defaultValues: customerFormDefaultValues,
  });

  useEffect(() => {
    if (!isOpen) {
      reset(customerFormDefaultValues);
    }
  }, [isOpen, reset]);

  const refetchOptions = () => ({
    refetchQueries: [customersApi.getCustomerContacts],
    awaitRefetchQueries: true,
  });

  const onSubmit = useCallback(
    async (values: CustomerFormValues) => {
      const dto = mapFormToCreateInput(values);
      await handleCreateCustomerAccount(dto, refetchOptions);

      handleClose();
    },
    [handleCreateCustomerAccount, handleClose]
  );

  return (
    <CustomDialog
      maxWidth="lg"
      isOpen={isOpen}
      onClose={handleClose}
      label="Add New Customer"
      actions={
        <>
          <Button variant="outlined" disabled={isCreating} onClick={handleClose}>
            Cancel
          </Button>

          <Button variant="contained" loading={isCreating} onClick={handleSubmit(onSubmit)}>
            Create Customer
          </Button>
        </>
      }
    >
      <CustomerAccountFormFields control={control} />
    </CustomDialog>
  );
};
